import isEqual from 'lodash-es/isEqual';

import EXPERIMENTS from '@rover/constants/src/platform/experiments.constants';
import { PRICE_FILTER } from '@rover/constants/src/search/searchPage.constants';
import type {
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import {
  effects as searchFiltersEffects,
  selectors as searchFiltersSelectors,
} from '@rover/react-lib/src/pages/search/SearchPage/SearchFilters.duck';
import { effects as searchModalEffects } from '@rover/react-lib/src/pages/search/SearchPage/SearchModal.duck';
import {
  convertToSearchApiKeys,
  convertToSearchFilterKeys,
  getNonFilterQueryParams,
  queryToFilters,
  setDefaultSearchValues,
  valuesToQueryStrings,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { selectors as countryConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/countryConfiguration.duck';
import { selectors as currentUserSelectors } from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import { selectors as experimentsSelectors } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { effects as petsEffects } from '@rover/react-lib/src/redux/ducks/pets.duck';
import {
  effects as searchEffects,
  selectors as searchSelectors,
} from '@rover/react-lib/src/redux/ducks/search.duck';
import {
  effects as serviceTypesEffects,
  selectors as serviceTypesSelectors,
} from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import appendUrlParams from '@rover/rsdk/src/modules/Network/appendUrlParams';
import { referrerIsSeoLanding } from '@rover/shared/js/utils/referrer';
import type { DataLayerEvent, SearchFilters } from '@rover/types';
import {
  PriceTransparencyLevel,
  PriceTransparencyLevels,
} from '@rover/types/src/PriceTransparencyLevel';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import events from './analytics';

export type State = Record<string, never>;

type Actions = Record<string, never>;

type Effects = {
  prefetchData: EffectCreator<void>;
  initialize: EffectCreator<void>;
  prefetchDataAndInitialize: EffectCreator<void>;
  fireAnalyticsEvent: EffectCreator<
    DataLayerEvent | ((filters: Partial<SearchFilters>) => DataLayerEvent)
  >;
  fireSearchSearchPageVisitEvent: EffectCreator<void>;
  fireSearchFiltersClickEvent: EffectCreator<void>;
  updateQueryParameters: EffectCreator<SearchFilters>;
  updateFiltersAndFireSearchOnChange: EffectCreator<Partial<SearchFilters>>;
  fireSearch: EffectCreator<
    | {
        searchInitiatedByMap: boolean;
      }
    | undefined
  >;
  fireSearchPageSearchCTAEvent: EffectCreator<void>;
  fireSearchPageCalendarClickEvent: EffectCreator<void>;
};

type Selectors = {
  getIsModalDisabled: SelectorCreator<boolean>;
  getPriceFilterMaxValue: SelectorCreator<number>;
  getPriceFilterMinValue: SelectorCreator<number>;
  getIsInDaycareGingrFacilitiesExperiment: SelectorCreator<boolean>;
  getIsInBoardingGingrFacilitiesExperiment: SelectorCreator<boolean>;
  getIsInSimplifiedMobileOwnerSearchFormExperiment: SelectorCreator<boolean>;
  getRollout1SimplifiedMobileOwnerSearchForm: SelectorCreator<boolean>;
  getRollout2SimplifiedMobileOwnerSearchForm: SelectorCreator<boolean>;
  getRollout3SimplifiedMobileOwnerSearchForm: SelectorCreator<boolean>;
  getRolloutPriceTransparency: SelectorCreator<boolean>;
  getIsInPetCountersExperiment: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'searchPage',
  initialState: {},
  actions: {},
  effects: {
    prefetchData:
      () =>
      async (
        dispatch,
        getState,
        duckActions,
        duckEffects,
        duckSelectors,
        { query }
      ): Promise<void> => {
        // `prefetchData` runs on the server, so `query` is the URL query params
        // (snake cased).
        // FIXME: Check if we can use queryToFilters here and delete convertToSearchFilterKeys
        const filters = convertToSearchFilterKeys({ query });

        const encodedFilters = valuesToQueryStrings(filters);
        const isUserAuthenticated = currentUserSelectors.isAuthenticated(getState());
        const serviceTypeOptions = searchFiltersSelectors.getServiceTypeFilterOptions(getState());
        const serviceType =
          serviceTypesSelectors.getServiceTypeBySlug(getState())(filters.serviceType || '') || {};

        const promises = [
          dispatch(
            searchEffects.fetchSearch({
              filters: setDefaultSearchValues(encodedFilters, {
                serviceType,
                serviceTypeOptions,
              }),
              isInitialSearch: true,
            })
          ),
          dispatch(
            searchEffects.fetchSearchOptions({
              allServices: true,
            })
          ),
          dispatch(serviceTypesEffects.fetchServiceTypes()),
        ];

        if (isUserAuthenticated) {
          promises.push(dispatch(petsEffects.fetchPets()));
        }

        await Promise.all(promises);
      },
    initialize:
      () =>
      async (
        dispatch,
        getState,
        duckActions,
        duckEffects,
        { getRolloutPriceTransparency, getIsInPetCountersExperiment }
      ): Promise<void> => {
        const state = getState();
        const rolloutPriceTransparency = getRolloutPriceTransparency(state);
        const isInPetCountersExperiment = getIsInPetCountersExperiment(state);
        const isBHUser = currentUserSelectors.getIsBrightHorizonsUser(getState());
        if (rolloutPriceTransparency || isInPetCountersExperiment || isBHUser) {
          await dispatch(searchFiltersEffects.initFiltersFromQueryParamsWithPetCounters());
        } else {
          await dispatch(searchFiltersEffects.initFiltersFromQueryParams());
        }
        await dispatch(searchModalEffects.initialize());
      },
    prefetchDataAndInitialize:
      () =>
      async (dispatch, getState, duckActions, { prefetchData, initialize }): Promise<void> => {
        await dispatch(prefetchData());
        await dispatch(initialize());
      },
    fireAnalyticsEvent:
      (eventData) =>
      async (dispatch, getState, duckActions, duckEffects, duckSelectors, { query }) => {
        if (typeof eventData === 'function') {
          const filters = queryToFilters(query || {}, {});
          // We are adding the hasFacilitiesInResults property to the event data
          // only if the event is SearchSearchPageVisitEvent
          if (eventData(filters).event === events.SearchSearchPageVisitEvent(filters).event) {
            const hasFacilitiesInResults = searchSelectors.getSearchResultsHasFacilities(
              getState()
            );
            fireDataLayerEvent({
              ...eventData(filters),
              hasFacilitiesInResults,
            });
          } else {
            fireDataLayerEvent({ ...eventData(filters) });
          }
        } else {
          fireDataLayerEvent({ ...eventData });
        }
      },
    fireSearchSearchPageVisitEvent: () => async (dispatch, getState, duckActions, duckEffects) => {
      await Promise.all([
        dispatch(duckEffects.fireAnalyticsEvent(events.SearchSearchPageVisitEvent)),
        dispatch(duckEffects.fireAnalyticsEvent(events.SearchSearchPageVisitGaEvent)),
      ]);
    },
    fireSearchFiltersClickEvent: () => async (dispatch, getState, duckActions, duckEffects) => {
      dispatch(duckEffects.fireAnalyticsEvent(events.SearchFiltersClickEvent));
    },
    updateQueryParameters:
      (filters) =>
      async (
        dispatch,
        getState,
        duckActions,
        duckEffects,
        duckSelectors,
        { history, routerProps }
      ) => {
        if (history && filters) {
          const url = (routerProps && routerProps.match && routerProps.match.url) || '';
          const urlParams = { ...filters };
          const snakeParams = convertToSearchApiKeys({
            filters: urlParams,
            allowAllKeys: true,
          });
          history.pushState(filters, '', appendUrlParams(url, snakeParams));
        }
      },
    fireSearch:
      (
        { searchInitiatedByMap } = {
          searchInitiatedByMap: false,
        }
      ) =>
      async (
        dispatch,
        getState,
        duckActions,
        { fireAnalyticsEvent, updateQueryParameters },
        { getRolloutPriceTransparency, getIsInPetCountersExperiment },
        { query }
      ): Promise<void> => {
        // `query` here is coming from the redux store, where it is stored as a
        // camel cased version of the URL query params.
        const state = getState();
        const nonFiltersQuery = getNonFilterQueryParams(query);
        const filters = searchFiltersSelectors.getQuery(state);
        const serviceTypeOptions = searchFiltersSelectors.getServiceTypeFilterOptions(state);
        const isCatOnly = searchFiltersSelectors.getIsCatOnly(state);
        const rolloutPriceTransparency = getRolloutPriceTransparency(state);
        const isPetCountersExperiment =
          getIsInPetCountersExperiment(state) && !searchSelectors.getHasPets(state);
        const excludedPersonOpks = searchSelectors.getExcludedPersonOpks(state);

        if (query) {
          const searchQuery = searchSelectors.getSearchQuery(state);

          if (searchQuery?.bestMatchOffset) {
            filters.bestMatchOffset = searchQuery.bestMatchOffset;
          }
        }

        const { dogCount, catCount, puppyCount, ...legacyFilters } = filters;
        const isBHUser = currentUserSelectors.getIsBrightHorizonsUser(getState());
        const priceTransparencyPetCounterFilters =
          rolloutPriceTransparency || isPetCountersExperiment || isBHUser
            ? { dogCount, catCount, puppyCount }
            : {};

        const serviceType = isCatOnly
          ? serviceTypesSelectors.getServiceTypeBySlug(state)(ServiceTypeSlug.DROP_IN) || {}
          : serviceTypesSelectors.getServiceTypeBySlug(state)(filters.serviceType || '') || {};

        const newQuery = {
          ...nonFiltersQuery,
          ...legacyFilters,
          ...priceTransparencyPetCounterFilters,
        };

        dispatch(fireAnalyticsEvent(events.PerformSearchEvent(filters)));
        dispatch(fireAnalyticsEvent(events.LocationTypeSearchEvent));

        const fetchSearchResponse = await dispatch(
          searchEffects.fetchSearch({
            filters: {
              ...setDefaultSearchValues(filters, {
                serviceType,
                serviceTypeOptions,
                excludedPersonOpks,
              }),
            },
            isInitialSearch: false,
            searchInitiatedByMap,
          })
        );

        // fetchSearchResponse is undefined if the API call failed or was cancelled
        if (fetchSearchResponse) {
          await dispatch(updateQueryParameters(newQuery));
        }

        await dispatch(searchFiltersEffects.clearDirty());
      },
    updateFiltersAndFireSearchOnChange:
      (filters) =>
      async (dispatch, getState, duckActions, { fireSearch }) => {
        const oldFilters = searchFiltersSelectors.getFilters(getState());
        await dispatch(searchFiltersEffects.updateFilters(filters));

        if (!isEqual(oldFilters, searchFiltersSelectors.getFilters(getState()))) {
          await dispatch(fireSearch());
        }
      },
    fireSearchPageSearchCTAEvent: () => async (dispatch, getState) => {
      const filters = searchFiltersSelectors.getFilters(getState());
      const eventData = events.SearchPageSearchCTA(filters);
      fireDataLayerEvent({ ...eventData });
    },
    fireSearchPageCalendarClickEvent: () => async (dispatch, getState) => {
      const filters = searchFiltersSelectors.getFilters(getState());
      const eventData = events.SearchPageCalendarClick(filters);
      fireDataLayerEvent({ ...eventData });
    },
  },
  selectors: {
    getPriceFilterMaxValue: (getState, createSelector) =>
      createSelector(
        [
          searchSelectors.getSearchPriceFilterMaxValue,
          countryConfigurationSelectors.getPriceFilterMaxValue,
        ],
        (searchPriceFilterMaxValue, configPriceFilterMaxValue) =>
          searchPriceFilterMaxValue || configPriceFilterMaxValue || PRICE_FILTER.DEFAULT_MAX_PRICE
      ),
    getPriceFilterMinValue: (getState, createSelector) =>
      createSelector(
        [
          searchSelectors.getSearchPriceFilterMinValue,
          countryConfigurationSelectors.getPriceFilterMinValue,
        ],
        (searchPriceFilterMinValue, configPriceFilterMinValue) =>
          searchPriceFilterMinValue || configPriceFilterMinValue || PRICE_FILTER.DEFAULT_MIN_PRICE
      ),
    getIsInDaycareGingrFacilitiesExperiment: (getState, createSelector) =>
      createSelector(
        [experimentsSelectors.isVariant],
        (isVariant) =>
          isVariant(EXPERIMENTS.GINGR_DAYCARE_FACILITIES_INCLUDED_IN_SEARCH_EXPERIMENT) &&
          isVariant(EXPERIMENTS.GINGR_DAYCARE_FACILITIES_INCLUDED_IN_SEARCH_RESULTS_EXPERIMENT)
      ),
    getIsInBoardingGingrFacilitiesExperiment: (getState, createSelector) =>
      createSelector(
        [experimentsSelectors.isVariant],
        (isVariant) =>
          isVariant(EXPERIMENTS.GINGR_BOARDING_FACILITIES_INCLUDED_IN_SEARCH_EXPERIMENT) &&
          isVariant(EXPERIMENTS.GINGR_BOARDING_FACILITIES_INCLUDED_IN_SEARCH_RESULTS_EXPERIMENT)
      ),
    getIsInSimplifiedMobileOwnerSearchFormExperiment: (getState, createSelector) =>
      createSelector([experimentsSelectors.isVariant], (isVariant) =>
        isVariant(EXPERIMENTS.SIMPLIFIED_MOBILE_OWNER_SEARCH_FORM_EXPERIMENT)
      ),
    getRollout1SimplifiedMobileOwnerSearchForm: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT1_SIMPLIFIED_MOBILE_OWNER_SEARCH_FORM)
      ),
    getRollout2SimplifiedMobileOwnerSearchForm: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT2_SIMPLIFIED_MOBILE_OWNER_SEARCH_FORM)
      ),
    getRollout3SimplifiedMobileOwnerSearchForm: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT3_SIMPLIFIED_MOBILE_OWNER_SEARCH_FORM)
      ),
    getRolloutPriceTransparency: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT_CALIFORNIA_PRICE_TRANSPARENCY)
      ),
    getIsInPetCountersExperiment: (getState, createSelector) =>
      createSelector(
        [frontendConfigurationSelectors.isFlagActive, searchSelectors.getPriceTransparencyLevel],
        (isFlagActive, priceTransparencyLevel: PriceTransparencyLevel) =>
          priceTransparencyLevel !== PriceTransparencyLevels.full &&
          isFlagActive(FLAGS.ROLLOUT_PET_COUNTER)
      ),
    getIsModalDisabled: (getState, createSelector) =>
      createSelector(
        [searchFiltersSelectors.getFilters, experimentsSelectors.isVariant],
        (filters: SearchFilters, isVariant) => {
          if (isVariant(EXPERIMENTS.BYPASS_SEARCH_MODAL_EXPERIMENT) && referrerIsSeoLanding()) {
            return true;
          }
          return !!filters.bypassInterstitial;
        }
      ),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
